import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ProductCardModel,
  ProductLabelModel,
  ProductTileModel,
  Text,
} from '@notino/react-styleguide';
import { masterUrl } from '@notino/shared/client-utils';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';

import { useSettings } from '@containers/ProductDetailContainer/hooks/useSettings';
import { useIsDualPrice } from '@containers/ProductDetailContainer/ProductDetail/hooks/useIsDualPrice';
import { resolveVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/useResolvedVoucherDiscount';
import { isConditionalDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/utils';
import { isDiorBrand } from '@containers/ProductDetailContainer/ProductDetail/utils';
import { AttributeTypes } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { ATTRIBUTION_TOKEN_QUERY_PARAM } from '@utils/constants';
import { noop } from '@utils/noop';
import { appendQueryParam } from '@utils/url';

import { useGetImageAttributes } from '../../CdnImage/useImageAttributes';
import { useProductSpaRedirect } from '../../ClientRedirect/useHandleClientRedirect';
import { useFormatPrice } from '../../PriceLabel/useFormattedPrice';
import { usePriceConfig } from '../../PriceLabel/usePriceConfig';
import { useWishlistActionsFactory } from '../WishlistModifier/useWishlistActions';
import { useWishlistLists } from '../WishlistModifier/useWishlistLists';
import { useWishlistSelectionDrawer } from '../WishlistModifier/useWishlistSelectionDrawer';

import { BuyButton } from './components';
import { ProductTileConditionalDiscount } from './components/ProductTileConditionalDiscount';
import { ProductTileConditionalFreeDelivery } from './components/ProductTileConditionalFreeDelivery';
import { ProductTileDiscount } from './components/ProductTileDiscount';
import { messages } from './messages';
import { IProductItemProps } from './model';
import { useGetTagAttributes } from './useTagAttributes';
import { useGetProductIcons } from './utils';

// this character ensures that there is same spacing as if text was present
// found here https://jkorpela.fi/chars/spaces.html
const SPECIAL_IDEOGRAPHIC_SPACE = '　';

type UseGetProductItemProps = Pick<
  IProductItemProps,
  | 'useMasterUrl'
  | 'setProductWishlisted'
  | 'GTMEventLabel'
  | 'inSectionOfType'
  | 'sendGTM'
  | 'noPriceIfUnavailable'
  | 'showWishlistButton'
  | 'onProductAdded'
  | 'onProductAddFailed'
  | 'onClosingCartModal'
  | 'showBuyButton'
  | 'showAddToCartModal'
  | 'buttonElementClassName'
  | 'canSpaRedirect'
  | 'wishlistActionLocation'
  | 'hasRegisteredWatchdog'
  | 'listIdForWishlistOperations'
  | 'iconsSubstitution'
>;

type GetProductProps = Pick<
  IProductItemProps,
  | 'product'
  | 'order'
  | 'appendComponent'
  | 'description'
  | 'position'
  | 'attributionToken'
  | 'shouldHideItemBuyButton'
  | 'onClickOveride'
  | 'buyButtonDisabled'
  | 'pictogramsHidden'
  | 'isSponsored'
  | 'selectable'
  | 'selected'
  | 'isLoading'
>;

type ProductItem = NonNullable<
  GetProductsByIdBatchedQuery['vpProductByIds'][number]
>;

const resolveProductHref = ({
  url,
  useMasterUrl,
  attributionToken,
}: {
  url: ProductItem['url'];
  useMasterUrl: boolean;
  attributionToken?: string;
}) => {
  const resolvedUrl = useMasterUrl ? masterUrl(url ?? '') : url ?? '';
  return attributionToken
    ? appendQueryParam(
        resolvedUrl,
        ATTRIBUTION_TOKEN_QUERY_PARAM,
        attributionToken
      )
    : resolvedUrl;
};

const getProductRatings = ({
  reviewOverview,
  useNewProductCard,
  inSectionOfType,
}: {
  reviewOverview: ProductItem['reviewOverview'];
  useNewProductCard: boolean | null;
  inSectionOfType: string;
}) => {
  if (!reviewOverview?.rating) {
    return undefined;
  }

  const ratings = {
    score: reviewOverview.rating,
    numReviews: reviewOverview.count,
  };

  return useNewProductCard ? ratings : { inSectionOfType, ...ratings };
};

const getProductCardState = ({
  useNewProductCard,
  isLoading,
}: {
  useNewProductCard: boolean | null;
  isLoading?: boolean;
}) => {
  if (!useNewProductCard) {
    return ProductTileModel.State.product;
  }

  return isLoading
    ? ProductCardModel.State.placeholder
    : ProductCardModel.State.product;
};

const getSponsoredProps = ({
  useNewProductCard,
  isSponsored,
  formatMessage,
}: {
  useNewProductCard: boolean | null;
  isSponsored?: boolean;
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
}) => {
  if (useNewProductCard) {
    return {
      sponsored: isSponsored
        ? {
            label: formatMessage(messages.sponsored),
            infoText: formatMessage(messages.sponsoredTooltip),
          }
        : undefined,
    };
  }

  return {
    sponsoredLabel: isSponsored ? formatMessage(messages.sponsored) : undefined,
    sponsoredLabelInfoText: isSponsored
      ? formatMessage(messages.sponsoredTooltip)
      : undefined,
  };
};

export const useGetProductItemProps = ({
  useMasterUrl = false,
  setProductWishlisted,
  GTMEventLabel,
  inSectionOfType = '',
  sendGTM = false,
  noPriceIfUnavailable,
  showWishlistButton = true,
  onProductAdded = noop,
  onProductAddFailed = noop,
  onClosingCartModal = noop,
  showBuyButton = false,
  showAddToCartModal = true,
  buttonElementClassName,
  canSpaRedirect = true,
  wishlistActionLocation,
  hasRegisteredWatchdog,
  listIdForWishlistOperations,
  iconsSubstitution,
}: UseGetProductItemProps) => {
  const { data: { currentLocale, currentCurrency } = {} } = usePriceConfig();
  const { Settings: { showUnitPrices } = {} } = useSettings();
  const { fetchLists, lastFetchedListsRef } = useWishlistLists();
  const { release_discovery_product_card_2026 } = useFeatureFlags();
  const { formatMessage } = useIntl();
  const formatPrice = useFormatPrice();
  const {
    isDualPrice,
    getLeftSideFormattedPrice,
    getRightSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
  } = useIsDualPrice();
  const getTagAttributes = useGetTagAttributes();
  const getProductIcons = useGetProductIcons();
  const getImageAttributes = useGetImageAttributes();

  const withProductSpaRedirect = useProductSpaRedirect(
    canSpaRedirect,
    useMasterUrl
  );

  const { handleWishlistClick } = useWishlistActionsFactory({
    onCompleted: (products) =>
      products.forEach((p) =>
        setProductWishlisted?.(p.productId, p.wishlisted)
      ),
  });

  const { open: openListSelection } = useWishlistSelectionDrawer({
    onSave: async ({
      addedToLists,
      removedFromLists,
      productIds,
      listPosition,
    }) => {
      await handleWishlistClick({
        productIds,
        actionLocation: wishlistActionLocation!,
        listName: GTMEventLabel,
        listPosition,
        lists: lastFetchedListsRef.current ?? [],
        addToLists: addedToLists,
        removeFromLists: removedFromLists,
      });
    },
  });

  return ({
    product,
    order,
    appendComponent,
    description,
    position = 0,
    attributionToken,
    shouldHideItemBuyButton,
    onClickOveride,
    buyButtonDisabled,
    pictogramsHidden,
    isSponsored,
    selectable,
    selected,
    isLoading,
  }: GetProductProps):
    | ProductCardModel.IProductCardContentProps
    | ProductTileModel.IProductTileContentProps
    | null => {
    if (!currentLocale || !currentCurrency || !product) {
      return null;
    }

    const formattedPriceObj = formatPrice(
      product.price as Parameters<typeof formatPrice>[0]
    );
    const formattedUnitPriceObj = formatPrice(
      product.unitPrice as Parameters<typeof formatPrice>[0]
    );
    const tagAttributes = getTagAttributes(
      {
        ...product.attributes,
      },
      {
        multiple: true,
      }
    );

    const productIcons = getProductIcons(product.attributes);

    const isDior = isDiorBrand(product.brand?.id);

    const imageAttributes = getImageAttributes(
      product.imageUrl ?? '',
      'list',
      `${product.brand?.name ?? ''} ${product.name ?? ''}`
    );

    const handleTracking = () => {
      if (sendGTM && currentCurrency) {
        dispatchTrackingEvent({
          event: 'select_item',
          product: [
            ProductEventWither()
              .withVpProduct(
                product as Parameters<
                  ReturnType<typeof ProductEventWither>['withVpProduct']
                >[0]
              )
              .withDiscountPrices(product.attributes, {
                shouldSendOnlyDiscountPrice: true,
              })
              .withAdditionalData({
                list_position: position,
                list_name: GTMEventLabel,
              })
              .build(),
          ],
          _clear: true,
        });
      }
    };

    const href = resolveProductHref({
      url: product.url,
      useMasterUrl,
      attributionToken,
    });

    const withSpaRedirect = withProductSpaRedirect({
      ...(product as { id: string; masterId: number; catalogMasterId: number }),
      url: href,
    });
    const handleClick = onClickOveride ?? withSpaRedirect(handleTracking);

    const productInStock =
      product.stockAvailability?.code !== IStock.outOfStock;
    const showPrice = !noPriceIfUnavailable || product.canBuy;

    const voucherDiscount = resolveVoucherDiscount(product);

    const shouldShowTags = productInStock && tagAttributes && !isDior;

    const ratings = getProductRatings({
      reviewOverview: product.reviewOverview,
      useNewProductCard: release_discovery_product_card_2026,
      inSectionOfType,
    });

    const additionalContent = (
      <>
        {appendComponent}
        {showBuyButton && !shouldHideItemBuyButton && (
          <BuyButton
            disabled={buyButtonDisabled}
            onProductAddFailed={onProductAddFailed}
            onProductAdded={onProductAdded}
            buttonElementClassName={buttonElementClassName!}
            product={product}
            showAddToCartModal={showAddToCartModal}
            disableRedirect={true}
            onClosingCartModal={onClosingCartModal}
            hasRegisteredWatchdog={hasRegisteredWatchdog}
          />
        )}
      </>
    );
    const sponsoredProps = getSponsoredProps({
      useNewProductCard: release_discovery_product_card_2026,
      isSponsored,
      formatMessage,
    });

    return {
      state: getProductCardState({
        useNewProductCard: release_discovery_product_card_2026,
        isLoading,
      }),
      tileProps: {
        'data-testid': 'product-item',
        'data-cypress': 'productItem-container',
      },
      iconsSubstitution,
      selectable,
      selected,
      locale: currentLocale.tag,
      product: {
        id: product.id ?? '',
        description: description,
        name: product.name ?? '',
        subName: product.subName ?? undefined,
        brandName: product.brand?.name ?? SPECIAL_IDEOGRAPHIC_SPACE,
        variantName: product.annotation ?? undefined,
      },
      unitPrice:
        (showPrice &&
          showUnitPrices &&
          product.unitPrice && {
            price: {
              price: String(
                formattedUnitPriceObj?.formattedPrice ||
                  product.unitPrice?.value
              ),
              currencySymbol: formattedUnitPriceObj?.currencySymbol ?? '',
              currencyFormat:
                formattedUnitPriceObj?.currentLocale?.priceFormat ?? '',
            },
            unit: product.unitPrice.unit ?? '',
            unitAmount: String(product.unitPrice.perAmount),
          }) ||
        undefined,
      ratings,
      productIcons: pictogramsHidden ? undefined : productIcons ?? undefined,
      productLabels:
        (shouldShowTags && {
          attributes: tagAttributes,
          labelStyleOverride: isDior
            ? ProductLabelModel.ProductStyles.neutral
            : undefined,
        }) ||
        undefined,
      aboveBuyButton: (
        <>
          {!product.canBuy && (
            <Text
              typography="labelSmall400"
              color="text.negative"
              marginTop="0.5rem"
            >
              <FormattedMessage
                {...(product.showWatchdog
                  ? messages.notAvailableText
                  : messages.disabledProductText)}
              />
            </Text>
          )}

          {voucherDiscount && !isConditionalDiscount(voucherDiscount) && (
            <ProductTileDiscount voucherDiscount={voucherDiscount} />
          )}

          {voucherDiscount && isConditionalDiscount(voucherDiscount) && (
            <ProductTileConditionalDiscount
              voucherDiscount={voucherDiscount}
              product={product as { brand: { name: string } }}
            />
          )}

          {product.attributes[AttributeTypes.ConditionalFreeDelivery] &&
            !voucherDiscount && (
              <ProductTileConditionalFreeDelivery
                delivery={
                  product.attributes[AttributeTypes.ConditionalFreeDelivery]
                }
                product={product as { brand: { name: string } }}
              />
            )}

          {release_discovery_product_card_2026 && additionalContent}
        </>
      ),
      appendComponent: release_discovery_product_card_2026
        ? undefined
        : additionalContent,
      href,
      imageProps: imageAttributes,
      onClick: handleClick,
      order,
      wishlist: showWishlistButton
        ? {
            wishlisted: product.wishlisted ?? false,
            onClick: async () => {
              const lists = await fetchLists();
              if (lists.length === 1 || listIdForWishlistOperations) {
                await handleWishlistClick({
                  productIds: [product.id!],
                  actionLocation: wishlistActionLocation!,
                  listName: GTMEventLabel,
                  listPosition: position,
                  notifyFailed: true,
                  addToLists: product.wishlisted
                    ? []
                    : [
                        {
                          id:
                            listIdForWishlistOperations ||
                            useWishlistLists.DEFAULT_LIST_ID,
                        },
                      ],
                  removeFromLists: product.wishlisted
                    ? [
                        {
                          id:
                            listIdForWishlistOperations ||
                            useWishlistLists.DEFAULT_LIST_ID,
                        },
                      ]
                    : [],
                  lists,
                });
                return;
              }
              openListSelection({
                lists,
                productId: product.id!,
                productName: `${product.brand?.name ?? ''} ${
                  product.name ?? ''
                }`,
              });
            },
          }
        : undefined,
      price:
        (showPrice &&
          (isDualPrice
            ? {
                price: getRightSideFormattedPrice(product.price!.value!),
                currencySymbol: rightSideCurrency,
                currencyFormat: 'v s',
              }
            : {
                price: String(
                  formattedPriceObj?.formattedPrice || product?.price?.value
                ),
                currencySymbol:
                  formattedPriceObj?.currencySymbol ||
                  currentCurrency.symbol ||
                  '',
                currencyFormat:
                  formattedPriceObj?.currentLocale?.priceFormat ||
                  currentLocale.priceFormat ||
                  '',
              })) ||
        undefined,
      dualPrice:
        (showPrice &&
          isDualPrice && {
            price: getLeftSideFormattedPrice(product.price!.value!),
            currencySymbol: leftSideCurrency,
            currencyFormat: 'v s',
          }) ||
        undefined,
      ...sponsoredProps,
    } as
      | ProductCardModel.IProductCardContentProps
      | ProductTileModel.IProductTileContentProps;
  };
};

export const useProductItemProps = ({
  product,
  description,
  position,
  appendComponent,
  order,
  onClickOveride,
  shouldHideItemBuyButton,
  buyButtonDisabled,
  pictogramsHidden,
  selectable,
  selected,
  isLoading,
  ...rest
}: IProductItemProps) => {
  const getProductItemProps = useGetProductItemProps(rest);

  return getProductItemProps({
    product,
    description,
    position,
    appendComponent,
    order,
    onClickOveride,
    shouldHideItemBuyButton,
    buyButtonDisabled,
    pictogramsHidden,
    selectable,
    selected,
    isLoading,
  });
};
